import React, { useCallback, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";
import { useSelector } from "react-redux";
import useMakeRequest from "../utils/ApiHelper";
import { ApiTypes } from "../constants/Constants";
import { ApiConstants } from "../constants/ApiConstants";
import contactUsValidation, { CONTACT_MESSAGE_MAX_CHAR } from "../Validation/ContactUsValidation";
import { LanguageConstants } from "../constants/LanguageConstants";
import { socket } from "../utils/Socket";
import "../component/layout/ContactSupportPage.scss";

interface FormInputs {
  name: string;
  email: string;
  subject: string;
  description: string;
  user_type: number;
}

type Props = {
  show: boolean;
  onHide: () => void;
};

const defaultFormValues: FormInputs = {
  user_type: 1,
  subject: "Contact support",
  name: "",
  email: "",
  description: "",
};

function ContactSupportModal({ show, onHide }: Props) {
  const contactAdd = useMakeRequest();
  const profiledata = useSelector((state: any) => state?.ProfileState);

  const { register, watch, setValue, reset, formState: { errors }, handleSubmit } = useForm<FormInputs>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: defaultFormValues,
    resolver: yupResolver(contactUsValidation),
  });

  const descriptionLen = (watch("description") ?? "").length;

  const prefillProfile = useCallback(() => {
    const profile = profiledata?.data?.data;
    if (!profile) return;
    setValue("name", profile.name ?? "", { shouldValidate: false, shouldDirty: false });
    setValue("email", profile.email ?? "", { shouldValidate: false, shouldDirty: false });
  }, [profiledata?.data?.data, setValue]);

  const resetModalState = useCallback(() => {
    contactAdd.reset();
    reset(defaultFormValues, { keepErrors: false, keepDirty: false, keepTouched: false });
  }, [contactAdd, reset]);

  const handleEntered = useCallback(() => {
    reset(defaultFormValues, { keepErrors: false, keepDirty: false, keepTouched: false });
    prefillProfile();
  }, [prefillProfile, reset]);

  const handleExited = useCallback(() => {
    resetModalState();
    requestAnimationFrame(() => {
      if (!document.querySelector(".modal.show")) {
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");
      }
    });
  }, [resetModalState]);

  const onSubmit = (payload: FormInputs) => {
    contactAdd.makeRequest({
      payload,
      type: ApiTypes.POST,
      url: ApiConstants?.AddContactUs,
      toastStatus: true,
    });
  };

  useEffect(() => {
    if (!contactAdd?.status) return;
    const socketpayload = {
      sender_type: "1",
      receiver_id: "1",
    };
    socket.emit("admin_count", socketpayload, async () => {});
    contactAdd.reset();
    onHide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactAdd?.status]);

  const handleClose = () => {
    if (contactAdd.loading) return;
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onEntered={handleEntered}
      onExited={handleExited}
      centered
      size="lg"
      enforceFocus={false}
      className="contact-support-bs-modal"
    >
      <Modal.Body className="p-0 border-0">
        <div className="contact-support-page contact-support-page--modal">
          <div className="contact-support-page__panel">
            <div className="contact-support-page__head">
              <h2 className="contact-support-page__title">{LanguageConstants?.FOOTER_CONTACT_SUPPORT}</h2>
              <button
                type="button"
                className="contact-support-page__close"
                onClick={handleClose}
                disabled={contactAdd.loading}
                aria-label={LanguageConstants?.CONTACT_SUPPORT_CLOSE_ARIA}
              >
                <span aria-hidden>×</span>
              </button>
            </div>
            <p className="contact-support-page__lead">{LanguageConstants?.CONTACT_SUPPORT_LEAD}</p>
            <form className="contact-support-page__form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="contact-support-page__field">
                <label className="contact-support-page__label" htmlFor="contact-support-modal-name">
                  {LanguageConstants?.NAME}
                </label>
                <input
                  id="contact-support-modal-name"
                  type="text"
                  className="contact-support-page__input"
                  autoComplete="name"
                  placeholder={LanguageConstants?.ENTER_FULL_NAME}
                  disabled={contactAdd.loading}
                  {...register("name")}
                />
                <ErrorMessage errors={errors} name="name" render={({ message }) => <p className="contact-support-page__error">{message}</p>} />
              </div>
              <div className="contact-support-page__field">
                <label className="contact-support-page__label" htmlFor="contact-support-modal-email">
                  {LanguageConstants?.EMAIL_ID}
                </label>
                <input
                  id="contact-support-modal-email"
                  type="email"
                  className="contact-support-page__input"
                  autoComplete="email"
                  placeholder={LanguageConstants?.ENTER_EMAIL_ID}
                  disabled={contactAdd.loading}
                  {...register("email")}
                />
                <ErrorMessage errors={errors} name="email" render={({ message }) => <p className="contact-support-page__error">{message}</p>} />
              </div>
              <div className="contact-support-page__field">
                <label className="contact-support-page__label" htmlFor="contact-support-modal-message">
                  {LanguageConstants?.MESSAGE}
                </label>
                <div className="contact-support-page__textarea-wrap">
                  <textarea
                    id="contact-support-modal-message"
                    className="contact-support-page__textarea"
                    rows={8}
                    maxLength={CONTACT_MESSAGE_MAX_CHAR}
                    placeholder={LanguageConstants?.CONTACT_SUPPORT_MSG_PLACEHOLDER}
                    disabled={contactAdd.loading}
                    {...register("description")}
                  />
                  <span className="contact-support-page__count">
                    {LanguageConstants?.CONTACT_SUPPORT_CHAR_COUNT} ({descriptionLen}/{CONTACT_MESSAGE_MAX_CHAR})
                  </span>
                </div>
                <ErrorMessage errors={errors} name="description" render={({ message }) => <p className="contact-support-page__error">{message}</p>} />
              </div>
              <button type="submit" className="contact-support-page__submit" disabled={contactAdd.loading}>
                {contactAdd.loading ? "Sending…" : LanguageConstants?.SEND}
              </button>
            </form>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ContactSupportModal;
